import React, { useCallback, useEffect, useState } from 'react';
import { alpha } from '@mui/material/styles';
import { THEME_COLORS } from '../../shared/constants/theme';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useTheme } from '@mui/material/styles';
import DatePicker from '../../shared/components/forms/HotelDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { DataTable } from '../../shared';
import {
  deleteStockInventory,
  getStockInventories,
  getStockSuppliers,
} from '../../services/api';
import {
  getStockListHeaderSubtitleSx,
  getStockListHeaderTitleSx,
  getStockListPaperSx,
} from '../../utils/stockMobileLayout';

const SEARCH_DEBOUNCE_MS = 350;

const StockInventoryListSection = ({
  isDark: isDarkProp,
  onAddInventory,
  onEditInventory,
  onViewInventory,
}) => {
  const theme = useTheme();
  const isDark = isDarkProp ?? theme.palette.mode === 'dark';
  const [loading, setLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [inventories, setInventories] = useState([]);
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [debouncedInvoiceSearch, setDebouncedInvoiceSearch] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: 'success',
    message: '',
  });
  const [pagination, setPagination] = useState({
    page: 0,
    perPage: 10,
    totalRecords: 0,
  });
  const [sortConfig, setSortConfig] = useState({
    column: 'date',
    direction: 'desc',
  });
  const [filters, setFilters] = useState({
    date: null,
    supplier: '',
    purchaseFor: '',
    invoiceSearch: '',
    amount: '',
    paymentType: '',
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [inventoryToDelete, setInventoryToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const showMessage = (severity, message) => {
    setSnackbar({ open: true, severity, message });
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedInvoiceSearch(filters.invoiceSearch);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [filters.invoiceSearch]);

  useEffect(() => {
    getStockSuppliers()
      .then((res) => {
        const names = (res?.suppliers || [])
          .map((supplier) => (supplier.name || '').trim())
          .filter(Boolean);
        setSupplierOptions(Array.from(new Set(names)).sort((a, b) => a.localeCompare(b)));
      })
      .catch(() => setSupplierOptions([]));
  }, []);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 0 }));
  }, [debouncedInvoiceSearch]);

  const fetchInventories = useCallback(async () => {
    setLoading(true);
    try {
      const sortParam = sortConfig.direction === 'desc'
        ? `-${sortConfig.column}`
        : sortConfig.column;
      const response = await getStockInventories({
        page: pagination.page + 1,
        per_page: pagination.perPage,
        sort: sortParam,
        date: filters.date || undefined,
        supplier: filters.supplier || undefined,
        purchase_for: filters.purchaseFor || undefined,
        invoice_search: debouncedInvoiceSearch || undefined,
        amount_min: filters.amount || undefined,
        payment_type: filters.paymentType || undefined,
      });
      setInventories(response?.inventories || []);
      setPagination((prev) => ({
        ...prev,
        totalRecords: Number(response?.total_records ?? 0),
      }));
      setHasLoadedOnce(true);
    } catch (error) {
      showMessage('error', error?.userMessage || 'Failed to load inventory list');
    } finally {
      setLoading(false);
    }
  }, [
    sortConfig.column,
    sortConfig.direction,
    pagination.page,
    pagination.perPage,
    filters.date,
    filters.supplier,
    filters.purchaseFor,
    filters.amount,
    filters.paymentType,
    debouncedInvoiceSearch,
  ]);

  useEffect(() => {
    fetchInventories();
  }, [fetchInventories]);

  const openDeleteDialog = (row) => {
    setInventoryToDelete({
      id: row.id,
      invoice_number: row.invoice_number || '',
      supplier_name: row.supplier_name || '',
      date: row.date || '',
    });
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    if (deleteLoading) return;
    setDeleteDialogOpen(false);
    setInventoryToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!inventoryToDelete?.id) return;
    setDeleteLoading(true);
    try {
      await deleteStockInventory(inventoryToDelete.id);
      showMessage('success', 'Stock record deleted successfully');
      setDeleteDialogOpen(false);
      setInventoryToDelete(null);
      await fetchInventories();
    } catch (error) {
      showMessage('error', error?.userMessage || 'Failed to delete inventory');
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns = [
    {
      field: 'date',
      label: 'Date',
      sortable: true,
      render: (value) => (value ? moment(value).format('DD MMM YYYY') : '—'),
    },
    { field: 'invoice_number', label: 'Invoice Number', sortable: true, render: (value) => value || '—' },
    { field: 'supplier_name', label: 'Supplier', sortable: true, render: (value) => value || '—' },
    {
      field: 'purchase_for',
      label: 'Type',
      sortable: true,
      render: (value) => {
        const v = String(value || '').toLowerCase();
        const isRestaurant = v === 'restaurant';
        const isHotel = v === 'hotel';
        if (!isRestaurant && !isHotel) return '—';
        return (
          <Chip
            size="small"
            label={isRestaurant ? 'RESTAURANT' : 'HOTEL'}
            sx={{
              backgroundColor: alpha(isRestaurant ? '#ffb74d' : '#4ce1b6', 0.14),
              color: isRestaurant ? '#ffb74d' : '#4ce1b6',
              fontWeight: 600,
            }}
          />
        );
      },
    },
    {
      field: 'total_amount',
      label: 'Amount',
      sortable: true,
      align: 'right',
      render: (value) => (
        <Typography sx={{ color: '#4ce1b6', fontWeight: 600 }}>
          ₹{Number(value || 0).toLocaleString('en-IN')}
        </Typography>
      ),
    },
    {
      field: 'payment_type',
      label: 'Payment Mode',
      sortable: false,
      render: (value) => (
        <Chip
          size="small"
          label={(value || 'unpaid').toUpperCase()}
          sx={{ backgroundColor: alpha('#4ce1b6', 0.12), color: '#4ce1b6' }}
        />
      ),
    },
    {
      field: 'actions',
      label: 'Actions',
      sortable: false,
      align: 'right',
      render: (value, row) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <IconButton size="small" onClick={() => onViewInventory(row.id)} title="View">
            <VisibilityOutlinedIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => onEditInventory(row.id)} sx={{ color: '#4ce1b6' }} title="Edit">
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => openDeleteDialog(row)} sx={{ color: '#ef5350' }} title="Delete">
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const filterRowCells = columns.map((column) => {
    switch (column.field) {
      case 'date':
        return (
          <LocalizationProvider key={column.field} dateAdapter={AdapterMoment}>
            <DatePicker
              value={filters.date ? moment(filters.date) : null}
              onChange={(newValue) => {
                setFilters((prev) => ({
                  ...prev,
                  date: newValue ? newValue.format('YYYY-MM-DD') : null,
                }));
                setPagination((prev) => ({ ...prev, page: 0 }));
              }}
              slotProps={{
                textField: {
                  placeholder: 'Filter Date',
                  size: 'small',
                  variant: 'standard',
                  InputProps: { disableUnderline: true },
                  sx: { width: '100%' },
                },
              }}
            />
          </LocalizationProvider>
        );
      case 'supplier_name':
        return (
          <FormControl key={column.field} size="small" sx={{ width: '100%' }}>
            <Select
              value={filters.supplier}
              onChange={(e) => {
                setFilters((prev) => ({ ...prev, supplier: e.target.value }));
                setPagination((prev) => ({ ...prev, page: 0 }));
              }}
              displayEmpty
              variant="standard"
              disableUnderline
            >
              <MenuItem value="">All</MenuItem>
              {supplierOptions.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case 'purchase_for':
        return (
          <FormControl key={column.field} size="small" sx={{ width: '100%' }}>
            <Select
              value={filters.purchaseFor}
              onChange={(e) => {
                setFilters((prev) => ({ ...prev, purchaseFor: e.target.value }));
                setPagination((prev) => ({ ...prev, page: 0 }));
              }}
              displayEmpty
              variant="standard"
              disableUnderline
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="hotel">Hotel</MenuItem>
              <MenuItem value="restaurant">Restaurant</MenuItem>
            </Select>
          </FormControl>
        );
      case 'invoice_number':
        return (
          <TextField
            key={column.field}
            placeholder="Search invoice..."
            size="small"
            value={filters.invoiceSearch || ''}
            onChange={(e) => {
              setFilters((prev) => ({ ...prev, invoiceSearch: e.target.value }));
              setPagination((prev) => ({ ...prev, page: 0 }));
            }}
            variant="standard"
            InputProps={{
              disableUnderline: true,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: '100%' }}
          />
        );
      case 'total_amount':
        return (
          <TextField
            key={column.field}
            placeholder="Amount ≥"
            type="number"
            size="small"
            value={filters.amount}
            onChange={(e) => {
              setFilters((prev) => ({ ...prev, amount: e.target.value }));
              setPagination((prev) => ({ ...prev, page: 0 }));
            }}
            variant="standard"
            InputProps={{
              disableUnderline: true,
              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
            }}
            sx={{ width: '100%' }}
          />
        );
      case 'payment_type':
        return (
          <FormControl key={column.field} size="small" sx={{ width: '100%' }}>
            <Select
              value={filters.paymentType}
              onChange={(e) => {
                setFilters((prev) => ({ ...prev, paymentType: e.target.value }));
                setPagination((prev) => ({ ...prev, page: 0 }));
              }}
              displayEmpty
              variant="standard"
              disableUnderline
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="paid">PAID</MenuItem>
              <MenuItem value="unpaid">UNPAID</MenuItem>
            </Select>
          </FormControl>
        );
      case 'actions':
        return (
          <Box key={column.field} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            {(filters.date ||
              filters.supplier ||
              filters.purchaseFor ||
              filters.invoiceSearch ||
              filters.amount ||
              filters.paymentType) && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setFilters({
                    date: null,
                    supplier: '',
                    purchaseFor: '',
                    invoiceSearch: '',
                    amount: '',
                    paymentType: '',
                  });
                  setPagination((prev) => ({ ...prev, page: 0 }));
                }}
              >
                Clear
              </Button>
            )}
          </Box>
        );
      default:
        return <div key={column.field} />;
    }
  });

  return (
    <>
      <Paper elevation={0} sx={getStockListPaperSx(isDark)}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '12px',
                backgroundColor: alpha('#4ce1b6', 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Inventory2OutlinedIcon sx={{ fontSize: 30, color: '#4ce1b6' }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={getStockListHeaderTitleSx(isDark)}>
                Stock list
              </Typography>
              <Typography variant="body2" sx={getStockListHeaderSubtitleSx(isDark)}>
                Manage stock purchase entries
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onAddInventory}
            sx={{
              backgroundColor: '#4ce1b6',
              color: '#000',
              '&:hover': {
                backgroundColor: '#3ab89a',
                color: '#000',
              },
            }}
          >
            Add Stock
          </Button>
        </Box>

        <DataTable
          columns={columns}
          data={inventories}
          loading={loading}
          skeletonRowCount={pagination.perPage}
          pagination={pagination}
          onPageChange={(page) => setPagination((prev) => ({ ...prev, page }))}
          onPerPageChange={(perPage) => setPagination((prev) => ({ ...prev, perPage, page: 0 }))}
          onSortChange={(newSort) => {
            setSortConfig(newSort);
            setPagination((prev) => ({ ...prev, page: 0 }));
          }}
          sortConfig={sortConfig}
          emptyMessage={
            hasLoadedOnce && !loading && inventories.length === 0
              ? 'No stock records found. Click "Add Stock" to create one.'
              : 'No records match the selected filters.'
          }
          stickyHeader
          optimizeScroll
          filterRow={filterRowCells}
        />
      </Paper>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          if (deleteLoading) return;
          handleDeleteCancel();
        }}
        PaperProps={{
          sx: {
            backgroundColor: isDark ? THEME_COLORS.SURFACE_ELEVATED_DARK : '#ffffff',
            border: `1px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
            minWidth: 400,
          },
        }}
      >
        <DialogTitle
          sx={{
            color: isDark ? '#fff' : '#212121',
            fontWeight: 600,
            pb: 2,
          }}
        >
          Delete inventory record
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{
              color: isDark ? '#ddd' : '#666',
              fontSize: '1rem',
            }}
          >
            Are you sure you want to delete this inventory record
            {inventoryToDelete?.invoice_number ? (
              <>
                {' '}
                for invoice <strong>&quot;{inventoryToDelete.invoice_number}&quot;</strong>
              </>
            ) : (
              inventoryToDelete?.id && (
                <>
                  {' '}
                  <strong>#{inventoryToDelete.id}</strong>
                </>
              )
            )}
            ?
            {(inventoryToDelete?.date || inventoryToDelete?.supplier_name) && (
              <>
                <br />
                <br />
                {inventoryToDelete.date && (
                  <>
                    Date: {moment(inventoryToDelete.date).format('DD MMM YYYY')}
                    {inventoryToDelete.supplier_name ? <br /> : null}
                  </>
                )}
                {inventoryToDelete.supplier_name && <>Supplier: {inventoryToDelete.supplier_name}</>}
              </>
            )}
            <br />
            <br />
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            p: 3,
            borderTop: `1px solid ${isDark ? alpha('#ffffff', 0.08) : '#e0e0e0'}`,
          }}
        >
          <Button
            onClick={handleDeleteCancel}
            disabled={deleteLoading}
            sx={{
              color: isDark ? '#999' : '#757575',
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={deleteLoading}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              '&:disabled': {
                backgroundColor: isDark ? '#555' : '#ccc',
              },
            }}
          >
            {deleteLoading ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: '#fff' }} />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default StockInventoryListSection;
